import { useState } from 'react';
import {
    RiMagicLine,
    RiMergeCellsHorizontal,
    RiDeleteBinLine,
    RiFileTextLine,
    RiCloseLine,
    RiLoader4Line
} from 'react-icons/ri';
import GlassCard from '../ui/v2/GlassCard';
import { profileService } from '../../services/profileService';
import { useProfile } from '../../context/ProfileContext';
import { notify } from '../../lib/notifications';
import { AnimatePresence, motion } from 'framer-motion';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    defaultResumeName?: string;
    defaultResumeId?: string;
}

export default function PopulateProfileModal({ isOpen, onClose, defaultResumeName, defaultResumeId }: Props) {
    const [isLoading, setIsLoading] = useState(false);
    const { refreshProfile } = useProfile();

    const handlePopulate = async (replace: boolean) => {
        setIsLoading(true);
        try {
            await profileService.populateFromResume(replace, defaultResumeId); // Pass specific ID if known, else undefined
            notify.success("Profile updated from resume successfully!");
            await refreshProfile();
            onClose();
        } catch (error) {
            console.error(error);
            notify.error("Failed to populate profile from resume");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal Content */}
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-2xl"
            >
                <GlassCard className="p-0 overflow-hidden shadow-2xl shadow-blue-900/20 border-blue-500/20">
                    {/* Header */}
                    <div className="p-6 border-b border-white/5 bg-gradient-to-r from-blue-500/10 to-transparent">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-xl bg-blue-500/20 text-blue-400">
                                    <RiMagicLine size={24} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-white">Auto-fill Profile</h2>
                                    <p className="text-sm text-blue-200/60">Use AI to extract details from your resume</p>
                                </div>
                            </div>
                            <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
                                <RiCloseLine size={24} />
                            </button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="p-6 space-y-6">
                        {/* Resume Info */}
                        <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/5">
                            <RiFileTextLine className="text-gray-400" size={20} />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-300">Using Resume source:</p>
                                <p className="text-sm font-bold text-white truncate">{defaultResumeName || "Default Resume"}</p>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="py-12 flex flex-col items-center justify-center text-center space-y-4">
                                <div className="relative">
                                    <div className="w-16 h-16 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <RiMagicLine className="text-blue-400 animate-pulse" size={24} />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white">Analyzing Resume...</h3>
                                    <p className="text-gray-400 text-sm max-w-xs mx-auto mt-1">
                                        Our AI is extracting your experience, education, and skills. This might take a moment.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Option 1: Merge */}
                                <button
                                    onClick={() => handlePopulate(false)}
                                    className="group relative flex flex-col text-left p-5 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-blue-500/50 hover:bg-blue-500/5 transition-all duration-300"
                                >
                                    <div className="mb-3 p-3 rounded-lg bg-emerald-500/10 text-emerald-400 w-fit group-hover:scale-110 transition-transform">
                                        <RiMergeCellsHorizontal size={24} />
                                    </div>
                                    <h3 className="text-lg font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">Merge & Append</h3>
                                    <p className="text-sm text-gray-400 leading-relaxed">
                                        Keep existing data and add new items found in your resume. Safe for partial updates.
                                    </p>
                                </button>

                                {/* Option 2: Replace */}
                                <button
                                    onClick={() => handlePopulate(true)}
                                    className="group relative flex flex-col text-left p-5 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-red-500/50 hover:bg-red-500/5 transition-all duration-300"
                                >
                                    <div className="mb-3 p-3 rounded-lg bg-red-500/10 text-red-400 w-fit group-hover:scale-110 transition-transform">
                                        <RiDeleteBinLine size={24} />
                                    </div>
                                    <h3 className="text-lg font-bold text-white mb-1 group-hover:text-red-400 transition-colors">Replace All</h3>
                                    <p className="text-sm text-gray-400 leading-relaxed">
                                        Clear current profile and fill freshly from resume. <span className="text-red-400/80">Destructive action.</span>
                                    </p>
                                </button>
                            </div>
                        )}
                    </div>
                </GlassCard>
            </motion.div>
        </div>
    );
}
